import { FunctionComponent, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  IonAvatar,
  IonButton,
  IonButtons,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonModal,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonText,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { close, logOut, personCircle } from "ionicons/icons";
import { useHeader } from "../hooks/use-header.hook";
import { getCsrfTokenFromCookie } from "../../../http-client/utils/csrf.utils";
import { useNetworkStatus } from "../../shared/hooks/use-network-status.hook";
import { useDebugLogs } from "../../shared/hooks/use-debug-logs.hook";
import { getEditAppBaseUrl } from "../../../config/edit-app.config";
import { exportOfflineCache } from "../../service-worker/communication.service";

export const Header: FunctionComponent = () => {
  const { t } = useTranslation();

  const {
    getUser,
    currentOrganization,
    onOrganizationSelectValueChanged,
    isPopoverOpened,
    togglePopover,
  } = useHeader();
  
  // Récupérer l'utilisateur de manière sécurisée
  let user = null;
  try {
    user = getUser();
  } catch {
    // L'utilisateur n'est pas encore chargé
  }

  const csrfToken = getCsrfTokenFromCookie() || "";
  const networkStatus = useNetworkStatus();
  const { logs, clearLogs } = useDebugLogs();
  
  // État pour gérer la visibilité des logs (masqué par défaut)
  const [showLogs, setShowLogs] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('debug-logs-visible');
      return stored === 'true';
    } catch {
      return false;
    }
  });
  
  // Sauvegarder la préférence dans localStorage
  useEffect(() => {
    try {
      localStorage.setItem('debug-logs-visible', String(showLogs));
    } catch {
      // Ignorer les erreurs de localStorage
    }
  }, [showLogs]);
  
  // Réf pour stocker le timestamp du dernier clic/tap sur l'icône dans la modal
  const lastClickRef = useRef<number>(0);
  
  // Gérer le double clic/tap sur l'icône utilisateur dans la modal
  const handleAvatarIconClick = (e: React.MouseEvent | React.TouchEvent) => {
    const currentTime = new Date().getTime();
    const timeSinceLastClick = currentTime - lastClickRef.current;
    
    // Si c'est un double clic/tap (moins de 300ms depuis le dernier clic)
    if (timeSinceLastClick < 300 && timeSinceLastClick > 0) {
      // Basculer la visibilité des logs
      e.preventDefault();
      e.stopPropagation();
      setShowLogs(prev => !prev);
      lastClickRef.current = 0; // Réinitialiser pour éviter les triple clics
    } else {
      // Premier clic, enregistrer le timestamp
      lastClickRef.current = currentTime;
    }
  };
  
  // Récupérer la version de l'application
  const appVersion = process.env.REACT_APP_VERSION || '0.0.0';

  const [isExportingCache, setIsExportingCache] = useState<boolean>(false);

  const handleExportOfflineCache = async () => {
    if (isExportingCache) return;
    setIsExportingCache(true);
    try {
      const payload = await exportOfflineCache(currentOrganization?.code ?? null, appVersion);
      if (payload.totalEntries === 0) {
        // eslint-disable-next-line no-alert
        alert(t("sites.header.exportOfflineCacheEmpty"));
        return;
      }
      const json = JSON.stringify(payload, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const orgaSlug = currentOrganization?.code ?? "all";
      link.download = `stonal-offline-cache-${orgaSlug}-${timestamp}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      console.error("[Header] Offline cache export failed", e);
      // eslint-disable-next-line no-alert
      alert(t("sites.header.exportOfflineCacheError"));
    } finally {
      setIsExportingCache(false);
    }
  };

  return (
    <IonHeader role="header" className="ion-no-border">
      <IonToolbar>
        <IonTitle>{t("sites.title")}</IonTitle>
        <IonButtons slot="start">
          <IonButton
            onClick={() => togglePopover(true)}
            color="medium"
            role="button"
            aria-label={t("sites.header.myAccount")}
          >
            <IonIcon slot="icon-only" icon={personCircle} />
          </IonButton>
        </IonButtons>
        <IonModal isOpen={isPopoverOpened}>
          <IonContent className="ion-padding-horizontal">
            <IonGrid className="ion-no-padding">
              <IonRow className="ion-margin-top ion-justify-content-between ion-align-items-center">
                {t("sites.header.myAccount")}
                <IonButton onClick={() => togglePopover(false)} color="dark" fill="clear" size="small">
                  <IonIcon slot="icon-only" icon={close} />
                </IonButton>
              </IonRow>
              <IonItem id="popover-user" className="ion-padding-vertical">
                <IonAvatar
                  onClick={handleAvatarIconClick}
                  onTouchEnd={handleAvatarIconClick}
                  style={{ cursor: 'pointer' }}
                >
                  <IonIcon size="large" icon={personCircle} />
                </IonAvatar>
                <IonLabel>
                  {user && (
                    <>
                      <p>
                        <strong>{user.fullName}</strong>
                      </p>
                      <p style={{ wordBreak: "break-all" }}>{user.userName}</p>
                    </>
                  )}
                </IonLabel>
              </IonItem>
              <IonItem lines={"inset"}>
                <IonSelect
                  label={t("sites.header.organization")}
                  labelPlacement='stacked'
                  role={"select"}
                  aria-label={t("sites.header.organization")}
                  interface={"action-sheet"}
                  value={currentOrganization?.code || ""}
                  cancelText={t("sites.header.cancelTextSelectOrganization")}
                  onIonChange={(e) => onOrganizationSelectValueChanged(e.detail!.value)}
                >
                  {user?.organizationList.map((organization) => (
                    <IonSelectOption key={organization.code} value={organization.code}>
                      {organization.name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
              <IonRow className="ion-justify-content-center ion-padding-horizontal ion-margin-top">
                <IonText color="medium" style={{ fontSize: "0.875rem" }}>
                  {networkStatus.displayLabel}
                </IonText>
              </IonRow>
              {/* Zone de logs de débogage */}
              {logs.length > 0 && showLogs && (
                <IonRow className="ion-padding-horizontal ion-margin-top">
                  <div style={{ 
                    width: '100%', 
                    maxHeight: '200px', 
                    overflowY: 'auto', 
                    backgroundColor: '#f5f5f5', 
                    padding: '8px', 
                    borderRadius: '4px',
                    fontSize: '0.7rem',
                    fontFamily: 'monospace'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', alignItems: 'center' }}>
                      <strong>Logs de débogage ({logs.length})</strong>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <IonButton 
                          size="small" 
                          fill="clear" 
                          onClick={async (e) => {
                            try {
                              const logsText = logs.map(log => 
                                `[${new Date(log.timestamp).toLocaleString()}] [${log.source}] ${log.message}`
                              ).join('\n');
                              await navigator.clipboard.writeText(logsText);
                              // Afficher un message de confirmation temporaire
                              const button = e.currentTarget as HTMLElement;
                              const originalText = button.textContent;
                              if (button) {
                                button.textContent = 'Copié!';
                                setTimeout(() => {
                                  if (button) button.textContent = originalText;
                                }, 2000);
                              }
                            } catch (err) {
                              console.error('Erreur lors de la copie:', err);
                            }
                          }}
                          style={{ fontSize: '0.7rem', height: '24px', minWidth: '60px' }}
                        >
                          Copier
                        </IonButton>
                        <IonButton 
                          size="small" 
                          fill="clear" 
                          onClick={clearLogs} 
                          style={{ fontSize: '0.7rem', height: '24px', minWidth: '60px' }}
                        >
                          Effacer
                        </IonButton>
                      </div>
                    </div>
                    {logs.slice(-20).reverse().map((log, index) => (
                      <div key={index} style={{ marginBottom: '4px', color: log.source === 'service-worker' ? '#0066cc' : '#666' }}>
                        <span style={{ color: '#999' }}>
                          {new Date(log.timestamp).toLocaleTimeString()}
                        </span>
                        {' '}
                        <strong>[{log.source}]</strong>
                        {' '}
                        {log.message}
                      </div>
                    ))}
                  </div>
                </IonRow>
              )}
              <IonRow className="ion-justify-content-center ion-padding-horizontal ion-margin-bottom">
                <IonText color="medium" style={{ fontSize: "0.75rem" }}>
                  v {appVersion}
                </IonText>
              </IonRow>
              <IonRow className="ion-justify-content-center ion-padding-horizontal ion-margin-bottom">
                <IonButton
                  color={"primary"}
                  role="button"
                  disabled={!currentOrganization?.code}
                  onClick={() => {
                    const codeOrga = currentOrganization?.code;
                    const baseUrl = getEditAppBaseUrl();
                    if (codeOrga && baseUrl) {
                      window.location.href = `${baseUrl}/edit/${codeOrga}/`;
                    }
                  }}
                >
                  {t("sites.header.newErgonomyButton")}
                </IonButton>
              </IonRow>
              <IonRow className="ion-justify-content-center ion-padding-vertical">
                <form action="/api/logout" method="POST">
                  <input type="hidden" name="_csrf" value={csrfToken} />
                  <IonButton
                    color={"secondary"}
                    role="button"
                    aria-label={t("sites.header.disconnectButton")}
                    type="submit"
                  >
                    <IonIcon slot="start" icon={logOut} />
                    <IonLabel>{t("sites.header.disconnectButton")}</IonLabel>
                  </IonButton>
                </form>
              </IonRow>
              <IonRow className="ion-justify-content-center ion-padding-horizontal ion-margin-bottom">
                <IonButton
                  fill="clear"
                  size="small"
                  color="medium"
                  role="button"
                  disabled={isExportingCache}
                  onClick={handleExportOfflineCache}
                >
                  {t("sites.header.exportOfflineCacheButton")}
                </IonButton>
              </IonRow>
            </IonGrid>
          </IonContent>
        </IonModal>
      </IonToolbar>
    </IonHeader>
  );
};
